import React, { useEffect, useState } from "react";
import { FaWhatsapp, FaLinkedin, FaGithub } from "react-icons/fa";
import { MdCheck, MdMailOutline, MdOutlineFileDownload } from "react-icons/md";
import { profile } from "./profile";
import { CountUp, useReveal, useScrollProgress, useScrollSpy } from "./motion";
import "./portfolio.scss";

const { contact } = profile;
const MAX_SKILL_YEARS = 11;
const VISIBLE_POINTS = 3;
const NAV = [
  { id: "results", label: "Results" },
  { id: "work", label: "Work" },
  { id: "experience", label: "Experience" },
  { id: "skills", label: "Skills" },
];
const NAV_IDS = NAV.map((n) => n.id);

function useClock(timeZone) {
  const format = () =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date());
  const [time, setTime] = useState(format);

  useEffect(() => {
    const id = setInterval(() => setTime(format()), 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZone]);

  return time;
}

function Header() {
  const active = useScrollSpy(NAV_IDS);
  const progressRef = useScrollProgress();

  return (
    <header className="site-header" ref={progressRef}>
      <div className="container site-header__inner">
        <a href="#top" className="site-header__name">
          {profile.name}
        </a>
        <nav className="site-header__nav" aria-label="Sections">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              aria-current={active === n.id ? "location" : undefined}
            >
              {n.label}
            </a>
          ))}
        </nav>
        <a href="#contact" className="btn btn--small btn--ink">
          Contact
        </a>
      </div>
      <span className="site-header__progress" aria-hidden="true" />
    </header>
  );
}

function ContactButtons({ compact }) {
  return (
    <div className={`actions${compact ? " actions--compact" : ""}`}>
      <a className="btn btn--primary" href={contact.whatsapp} target="_blank" rel="noreferrer">
        <FaWhatsapp aria-hidden="true" /> Message on WhatsApp
      </a>
      <a className="btn btn--outline" href={`mailto:${contact.email}`}>
        <MdMailOutline aria-hidden="true" /> Email me
      </a>
      {profile.cv && (
        <a className="btn btn--outline" href={profile.cv} download>
          <MdOutlineFileDownload aria-hidden="true" /> Download CV
        </a>
      )}
    </div>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="container hero__grid">
        <div className="hero__canvas">
          <p className="hero__status">
            <span className="hero__dot" aria-hidden="true" />
            Open to frontend lead roles in the UAE. {profile.availability}.
          </p>

          <div className="frame">
            <h1 className="frame__name">
              Varada <span>Narendra</span>
            </h1>
            <span className="frame__handle frame__handle--tl" aria-hidden="true" />
            <span className="frame__handle frame__handle--tr" aria-hidden="true" />
            <span className="frame__handle frame__handle--bl" aria-hidden="true" />
            <span className="frame__handle frame__handle--br" aria-hidden="true" />
            <p className="frame__badge">
              {profile.role}, {profile.years}
            </p>
          </div>

          <p className="hero__lead">{profile.lead}</p>

          <ContactButtons />

          <div className="hero__clients">
            <p>Products delivered for</p>
            <ul>
              {profile.clients.map((client) => (
                <li key={client}>{client}</li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="inspect" aria-labelledby="inspect-title">
          <div className="inspect__head">
            <img
              className="inspect__photo"
              src={`${process.env.PUBLIC_URL}${profile.photo}`}
              alt={`Portrait of ${profile.name}`}
              width="64"
              height="77"
            />
            <div>
              <h2 id="inspect-title" className="inspect__title">
                For recruiters
              </h2>
              <p className="inspect__sub">Your screening questions, answered</p>
            </div>
          </div>
          <dl className="inspect__list">
            {profile.facts.map((fact, i) => (
              <div className="inspect__row" key={fact.label} style={{ "--i": i }}>
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
          <div className="inspect__links">
            <a href={contact.linkedin} target="_blank" rel="noreferrer">
              <FaLinkedin aria-hidden="true" /> LinkedIn
            </a>
            <a href={contact.github} target="_blank" rel="noreferrer">
              <FaGithub aria-hidden="true" /> GitHub
            </a>
            <a href={contact.phoneHref}>{contact.phone}</a>
          </div>
        </aside>
      </div>
    </section>
  );
}

function LighthouseMeasure() {
  return (
    <figure
      className="measure"
      data-reveal=""
      aria-label="Lighthouse performance score raised from 74 to 96, a gain of 22 points"
    >
      <div className="measure__head">
        <p className="measure__value">
          74 <span className="measure__to" aria-hidden="true">to</span> <CountUp value="96" from={74} duration={1400} />
        </p>
        <figcaption>
          Lighthouse performance score on the Zebra Technologies platforms after bundle
          optimisation and lazy loading. The shaded band is Lighthouse's "good" range, 90 and up.
        </figcaption>
      </div>
      <div className="measure__scale" aria-hidden="true">
        <div className="measure__good" style={{ left: "90%", width: "10%" }} />
        <div className="measure__track" />
        <div className="measure__mark measure__mark--before" style={{ left: "74%" }}>
          <span>74</span>
        </div>
        <div className="measure__mark measure__mark--after" style={{ left: "96%" }}>
          <span>96</span>
        </div>
        <div className="measure__redline" style={{ left: "74%", width: "22%" }}>
          <span>+22</span>
        </div>
        <div className="measure__ticks">
          {[0, 50, 100].map((t) => (
            <span key={t} style={{ left: `${t}%` }}>
              {t}
            </span>
          ))}
        </div>
      </div>
    </figure>
  );
}

function Results() {
  return (
    <section className="section" id="results" aria-labelledby="results-title">
      <div className="container section__grid">
        <div className="section__head" data-reveal="">
          <h2 id="results-title">Results</h2>
          <p>Outcomes from shipped work, measured by the teams I worked with.</p>
        </div>
        <div className="section__body">
          <LighthouseMeasure />
          <ul className="results">
            {profile.results.map((r, i) => (
              <li key={r.label} className="results__item" data-reveal="" style={{ "--i": i % 2 }}>
                <p className="results__value">
                  <CountUp value={r.value} />
                </p>
                <div>
                  <p className="results__label">{r.label}</p>
                  <p className="results__detail">{r.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Work() {
  return (
    <section className="section" id="work" aria-labelledby="work-title">
      <div className="container section__grid">
        <div className="section__head" data-reveal="">
          <h2 id="work-title">Selected work</h2>
          <p>Enterprise products across banking, fintech, GenAI, e-commerce and IoT.</p>
        </div>
        <div className="section__body">
          <div className="featured">
            {profile.featuredWork.map((w) => (
              <article key={w.title} className="featured__item" data-reveal="">
                <p className="featured__client">
                  {w.client} <span>{w.via}</span>
                </p>
                <h3>{w.title}</h3>
                <p className="featured__summary">{w.summary}</p>
                <p className="featured__impact">{w.impact}</p>
                <p className="stack">{w.stack}</p>
              </article>
            ))}
          </div>
          <div className="more-work">
            {profile.moreWork.map((w, i) => (
              <article
                key={w.title}
                className="more-work__item"
                data-reveal=""
                style={{ "--i": i % 2 }}
              >
                <p className="more-work__client">{w.client}</p>
                <h3>{w.title}</h3>
                <p>{w.impact}</p>
                <p className="stack">{w.stack}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  if (!profile.testimonials.length) return null;

  return (
    <section className="section" id="testimonials" aria-labelledby="testimonials-title">
      <div className="container section__grid">
        <div className="section__head" data-reveal="">
          <h2 id="testimonials-title">Recommendations</h2>
          <p>From managers and colleagues, as written on LinkedIn.</p>
        </div>
        <div className="section__body quotes">
          {profile.testimonials.map((t) => (
            <figure key={t.name} className="quote" data-reveal="">
              <blockquote>
                <p>{t.quote}</p>
              </blockquote>
              <figcaption>
                <span className="quote__name">{t.name}</span>
                <span className="quote__role">{t.role}</span>
                {t.relation && <span className="quote__role">{t.relation}</span>}
              </figcaption>
            </figure>
          ))}
          <a className="quotes__more" href={contact.linkedin} target="_blank" rel="noreferrer">
            Read all recommendations on LinkedIn
          </a>
        </div>
      </div>
    </section>
  );
}

function Role({ job, current }) {
  const [open, setOpen] = useState(false);
  const shown = job.points.slice(0, VISIBLE_POINTS);
  const extra = job.points.slice(VISIBLE_POINTS);
  const moreId = `more-${job.company.replace(/\W+/g, "-").toLowerCase()}`;

  return (
    <li className={`role${current ? " role--current" : ""}`} data-reveal="">
      <p className="role__dates">
        {job.start} to {job.end}
      </p>
      <div className="role__body">
        <h3>
          {job.role}{" "}
          <span>
            at{" "}
            <a className="draw-link" href={job.url} target="_blank" rel="noreferrer">
              {job.company}
            </a>
          </span>
        </h3>
        <p className="role__summary">{job.summary}</p>
        <ul className="role__points">
          {shown.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
        {extra.length > 0 && (
          <>
            <div className={`role__more${open ? " is-open" : ""}`} id={moreId}>
              <div className="role__more-inner">
                <ul className="role__points">
                  {extra.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
            <button
              type="button"
              className="role__toggle"
              aria-expanded={open}
              aria-controls={moreId}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? "Show less" : `Show ${extra.length} more`}
            </button>
          </>
        )}
        <p className="stack">{job.stack}</p>
      </div>
    </li>
  );
}

function Experience() {
  return (
    <section className="section" id="experience" aria-labelledby="experience-title">
      <div className="container section__grid">
        <div className="section__head" data-reveal="">
          <h2 id="experience-title">Experience</h2>
          <p>From web designer in 2015 to leading a 20+ engineer team today.</p>
        </div>
        <ol className="section__body timeline">
          {profile.experience.map((job, i) => (
            <Role key={job.company} job={job} current={i === 0} />
          ))}
        </ol>
      </div>
    </section>
  );
}

function Skills() {
  return (
    <section className="section" id="skills" aria-labelledby="skills-title">
      <div className="container section__grid">
        <div className="section__head" data-reveal="">
          <h2 id="skills-title">Skills</h2>
          <p>A frontend specialist who also builds and ships the services behind the UI.</p>
        </div>
        <div className="section__body skills">
          <h3 className="skills__sub">Frontend</h3>
          <table className="years" data-reveal="">
            <caption className="visually-hidden">Frontend years of experience by skill</caption>
            <thead className="visually-hidden">
              <tr>
                <th scope="col">Skill</th>
                <th scope="col">Experience</th>
              </tr>
            </thead>
            <tbody>
              {profile.frontendYears.map((s, i) => (
                <tr key={s.skill} title={`${s.skill}: ${s.text}`} style={{ "--i": i }}>
                  <th scope="row">{s.skill}</th>
                  <td>
                    <span className="years__bar" aria-hidden="true">
                      <span style={{ width: `${(s.years / MAX_SKILL_YEARS) * 100}%` }} />
                    </span>
                    <span className="years__text">{s.text}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="skills__sub">
            Backend and cloud <span>at Eximietas Design and Elfonze</span>
          </h3>
          <ul className="backend" data-reveal="">
            {profile.backend.map((b) => (
              <li key={b.skill}>
                <p className="backend__skill">{b.skill}</p>
                <p className="backend__detail">{b.detail}</p>
                <p className="years__text">{b.text}</p>
              </li>
            ))}
          </ul>

          <h3 className="skills__sub">Tools and practices</h3>
          <dl className="skill-groups" data-reveal="">
            {profile.skillGroups.map((g) => (
              <div key={g.name}>
                <dt>{g.name}</dt>
                <dd>{g.items}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

function Credentials() {
  const { education } = profile;
  return (
    <section className="section" id="credentials" aria-labelledby="credentials-title">
      <div className="container section__grid">
        <div className="section__head" data-reveal="">
          <h2 id="credentials-title">Learning and education</h2>
          <p>Recent course certificates, each with a link to verify it.</p>
        </div>
        <div className="section__body credentials">
          <ul className="certs" data-reveal="">
            {profile.certifications.map((c) => (
              <li key={c.name}>
                <a className="draw-link" href={c.url} target="_blank" rel="noreferrer">
                  {c.name}
                </a>
                <p>
                  {c.provider} course certificate, {c.date}
                </p>
              </li>
            ))}
          </ul>
          <div className="education" data-reveal="">
            <h3>{education.degree}</h3>
            <p>
              {education.institution}, {education.years}
            </p>
            <p className="education__note">{education.note}</p>
          </div>
          <div className="education">
            <h3>Languages</h3>
            <p>{profile.languages}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const dubai = useClock("Asia/Dubai");
  const bangalore = useClock("Asia/Kolkata");
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(contact.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.location.href = `mailto:${contact.email}`;
    }
  };

  return (
    <section className="contact" id="contact" aria-labelledby="contact-title">
      <div className="container contact__inner" data-reveal="">
        <h2 id="contact-title">Hiring a frontend lead in the UAE?</h2>
        <p className="contact__lead">
          I am actively looking for frontend lead roles in the UAE and ready to relocate
          immediately, to Dubai, Abu Dhabi or anywhere in the country, with employment visa
          sponsorship. My notice period is 3 months and negotiable, my degree certificate
          attestation is already complete, and I am available for video interviews during UAE
          working hours.
        </p>

        <ContactButtons />

        <dl className="contact__details">
          <div>
            <dt>Email</dt>
            <dd>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
              <button
                type="button"
                className={`copy${copied ? " is-copied" : ""}`}
                onClick={copyEmail}
              >
                {copied && <MdCheck aria-hidden="true" />}
                {copied ? "Copied" : "Copy"}
              </button>
              <span className="visually-hidden" aria-live="polite">
                {copied ? "Email address copied" : ""}
              </span>
            </dd>
          </div>
          <div>
            <dt>Phone and WhatsApp</dt>
            <dd>
              <a href={contact.phoneHref}>{contact.phone}</a>
              {contact.phone2 && (
                <>
                  {" "}
                  / <a href={contact.phone2Href}>{contact.phone2}</a>
                </>
              )}
            </dd>
          </div>
          <div>
            <dt>Time now</dt>
            <dd>
              {dubai} in Dubai, {bangalore} in Bangalore
            </dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="container site-footer__inner">
        <p>© {new Date().getFullYear()} {profile.name}</p>
        <ul>
          <li>
            <a href={contact.linkedin} target="_blank" rel="noreferrer">
              LinkedIn
            </a>
          </li>
          <li>
            <a href={contact.github} target="_blank" rel="noreferrer">
              GitHub
            </a>
          </li>
          <li>
            <a href={`mailto:${contact.email}`}>Email</a>
          </li>
        </ul>
      </div>
    </footer>
  );
}

export default function Portfolio() {
  useReveal();

  return (
    <>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Header />
      <main id="main">
        <Hero />
        <Results />
        <Work />
        <Testimonials />
        <Experience />
        <Skills />
        <Credentials />
        <Contact />
      </main>
      <Footer />
    </>
  );
}
